// src/pages/AdminPackagesPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient.js";
import * as Icons from "lucide-react";

import DashboardSidebarAdmin from "../components/Dashboard/DashboardSidebarAdmin.jsx";
import DashboardNavbarAdmin from "../components/Dashboard/DashboardNavbarAdmin.jsx";

const AdminPackagesPage = () => {
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);

  const [packages, setPackages] = useState([]);
  const [features, setFeatures] = useState([]);
  const [packageFeatures, setPackageFeatures] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);

  const [formOpen, setFormOpen] = useState(false);
  const [formMode, setFormMode] = useState("create"); // "create" | "edit"
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const emptyForm = {
    id: null,
    name: "",
    price: "",
    base_credits: "",
    is_active: true,
    populer: false,
    beranda: false,
    featureIds: [],
  };

  const [formState, setFormState] = useState(emptyForm);

  // ---------- helper ----------
  const formatRupiah = (value) =>
    new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      maximumFractionDigits: 0,
    }).format(value || 0);

  const displayName =
    profile?.username || user?.email?.split("@")[0] || "Admin";

  const featuresByPackageId = useMemo(() => {
    const featureMap = new Map();
    features.forEach((f) => featureMap.set(f.id, f));
    const map = new Map();
    packageFeatures.forEach((pf) => {
      const feat = featureMap.get(pf.feature_id);
      if (!feat) return;
      if (!map.has(pf.package_id)) map.set(pf.package_id, []);
      map.get(pf.package_id).push(feat);
    });
    return map;
  }, [features, packageFeatures]);

  // ---------- cek user admin ----------
  useEffect(() => {
    let isMounted = true;

    const fetchUser = async () => {
      try {
        setLoadingUser(true);

        const { data, error: authError } = await supabase.auth.getUser();
        const authUser = data?.user;

        if (authError || !authUser) {
          navigate("/login");
          return;
        }

        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", authUser.id)
          .single();

        if (profileError && profileError.code !== "PGRST116") {
          throw profileError;
        }

        if (!profileData || !profileData.phone) {
          navigate("/login");
          return;
        }

        if (profileData.role !== "admin") {
          navigate("/dashboard");
          return;
        }

        if (!isMounted) return;

        setUser(authUser);
        setProfile(profileData);
      } catch (err) {
        console.error("Error fetching admin data:", err);
      } finally {
        if (isMounted) setLoadingUser(false);
      }
    };

    fetchUser();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  // ---------- load packages + features ----------
  const loadData = async () => {
    try {
      setLoadingData(true);
      setError(null);

      const [pkgRes, featRes, pkgFeatRes] = await Promise.all([
        supabase
          .from("credit_packages")
          .select("*")
          .order("id", { ascending: true }),
        supabase.from("app_features").select("*").eq("is_active", true).order("sort_order", { ascending: true }),
        supabase
          .from("package_features")
          .select("id, package_id, feature_id")
          .order("id", { ascending: true }),
      ]);

      if (pkgRes.error) throw pkgRes.error;
      if (featRes.error) throw featRes.error;
      if (pkgFeatRes.error) throw pkgFeatRes.error;

      const sortedPackages = (pkgRes.data || []).slice().sort((a, b) => {
        const pa = Number(a.price || 0);
        const pb = Number(b.price || 0);
        return pa - pb;
      });

      setPackages(sortedPackages);
      setFeatures(featRes.data || []);
      setPackageFeatures(pkgFeatRes.data || []);
    } catch (err) {
      console.error("Error loading packages data:", err);
      setError(err.message || "Gagal memuat data paket");
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (!loadingUser && user) {
      loadData();
    }
  }, [loadingUser, user]);

  // ---------- form handlers ----------
  const openCreateForm = () => {
    setFormMode("create");
    setFormState(emptyForm);
    setFormOpen(true);
  };

  const openEditForm = (pkg) => {
    const selectedFeatureIds = packageFeatures
      .filter((pf) => pf.package_id === pkg.id)
      .map((pf) => pf.feature_id);

    setFormMode("edit");
    setFormState({
      id: pkg.id,
      name: pkg.name || "",
      price:
        pkg.price !== null && pkg.price !== undefined ? String(pkg.price) : "",
      original_price: pkg.original_price || "",
      base_credits:
        pkg.base_credits !== null && pkg.base_credits !== undefined
          ? String(pkg.base_credits)
          : "",
      is_active: pkg.is_active ?? true,
      populer: !!pkg.populer,
      beranda: !!pkg.beranda,
      is_custom_button: !!pkg.is_custom_button,
      button_text: pkg.button_text || "",
      button_url: pkg.button_url || "",
      custom_features: pkg.custom_features || "",
      featureIds: selectedFeatureIds,
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setFormState(emptyForm);
  };

  const handleChange = (e) => {
    const { name, type, checked, value } = e.target;
    setFormState((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const toggleFeatureId = (featureId) => {
    setFormState((prev) => {
      const exists = prev.featureIds.includes(featureId);
      return {
        ...prev,
        featureIds: exists
          ? prev.featureIds.filter((id) => id !== featureId)
          : [...prev.featureIds, featureId],
      };
    });
  };

  // --- Centang semua fitur ---
  const allFeaturesSelected =
    features.length > 0 &&
    features.every((f) => formState.featureIds.includes(f.id));

  const handleToggleAllFeatures = () => {
    setFormState((prev) => ({
      ...prev,
      featureIds: allFeaturesSelected ? [] : features.map((f) => f.id),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const trimmedName = formState.name.trim();
    const priceNumber = Number(formState.price);
    const originalPriceNumber = formState.original_price ? Number(formState.original_price) : null;
    const creditsNumber = Number(formState.base_credits);

    if (!trimmedName) {
      setError("Nama paket wajib diisi.");
      return;
    }
    if (!Number.isFinite(priceNumber) || priceNumber < 0) {
      setError("Harga paket tidak boleh negatif.");
      return;
    }
    if (!Number.isInteger(creditsNumber) || creditsNumber <= 0) {
      setError("Jumlah kredit harus berupa angka bulat dan lebih dari 0.");
      return;
    }

    const payload = {
      name: trimmedName,
      price: priceNumber,
      original_price: originalPriceNumber,
      base_credits: creditsNumber,
      is_active: formState.is_active,
      populer: formState.populer,
      beranda: formState.beranda,
      is_custom_button: formState.is_custom_button,
      button_text: formState.button_text,
      button_url: formState.button_url,
      custom_features: formState.custom_features,
    };

    setSaving(true);
    try {
      let packageId = formState.id;

      if (formMode === "create") {
        const { data, error } = await supabase
          .from("credit_packages")
          .insert(payload)
          .select()
          .single();

        if (error) throw error;
        packageId = data.id;
      } else {
        const { error } = await supabase
          .from("credit_packages")
          .update(payload)
          .eq("id", packageId)
          .select()
          .single();

        if (error) throw error;
      }

      // update package_features: hapus semua dulu, lalu insert baru
      const { error: delError } = await supabase
        .from("package_features")
        .delete()
        .eq("package_id", packageId);

      if (delError) throw delError;

      if (formState.featureIds.length > 0) {
        const rows = formState.featureIds.map((fid) => ({
          package_id: packageId,
          feature_id: fid,
        }));
        const { error: insError } = await supabase
          .from("package_features")
          .insert(rows);
        if (insError) throw insError;
      }

      await loadData();
      closeForm();
    } catch (err) {
      console.error("Error saving package:", err);
      setError(err.message || "Gagal menyimpan paket.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (pkg) => {
    if (!window.confirm(`Yakin ingin menghapus paket "${pkg.name}"?`)) {
      return;
    }
    setError(null);
    setDeletingId(pkg.id);
    try {
      // hapus relasi fitur dulu
      const { error: delRelError } = await supabase
        .from("package_features")
        .delete()
        .eq("package_id", pkg.id);
      if (delRelError) throw delRelError;

      const { error } = await supabase
        .from("credit_packages")
        .delete()
        .eq("id", pkg.id);

      if (error) throw error;

      setPackages((prev) => prev.filter((p) => p.id !== pkg.id));
      setPackageFeatures((prev) =>
        prev.filter((pf) => pf.package_id !== pkg.id)
      );
    } catch (err) {
      console.error("Error deleting package:", err);
      setError(err.message || "Gagal menghapus paket.");
    } finally {
      setDeletingId(null);
    }
  };

  // ---------- loading user ----------
  if (loadingUser) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mb-3" />
        <p className="text-slate-500">Memuat admin dashboard...</p>
      </div>
    );
  }

  return (
    // ⬇️ di sini kita kunci tinggi viewport & matikan scroll global
    <div className="h-screen bg-slate-50 font-sans overflow-hidden">
      {/* flex penuh, sidebar + konten */}
      <div className="flex h-full min-w-0">
        {/* SIDEBAR ADMIN */}
        <DashboardSidebarAdmin />

        {/* AREA KANAN */}
        <div className="flex-1 flex flex-col min-w-0 h-full">
          {/* NAVBAR ADMIN (tetap) */}
          <DashboardNavbarAdmin displayName={displayName} />

          {/* KONTEN: bagian ini saja yang scroll */}
          <main className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
            <div className="max-w-6xl mx-auto space-y-4 sm:space-y-6">
              {/* Header + action */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
                    Paket Kredit
                  </h1>
                  <p className="text-sm sm:text-base text-slate-600">
                    Kelola paket kredit, harga, dan fitur yang disertakan di setiap paket.
                  </p>
                </div>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={openCreateForm}
                    className="inline-flex items-center justify-center px-3 sm:px-4 py-2 rounded-xl text-sm font-semibold bg-sky-600 text-white shadow-sm hover:bg-sky-700 active:scale-95 transition"
                  >
                    <span className="mr-1.5 text-base">＋</span>
                    Tambah Paket
                  </button>
                </div>
              </div>

              {/* Error global */}
              {error && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs sm:text-sm text-rose-700">
                  {error}
                </div>
              )}

              {/* List paket */}
              <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-3 sm:p-4 lg:p-6">
                {loadingData ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin h-8 w-8 border-4 border-sky-500 border-t-transparent rounded-full" />
                      <p className="text-xs sm:text-sm text-slate-500">
                        Memuat data paket...
                      </p>
                    </div>
                  </div>
                ) : packages.length === 0 ? (
                  <div className="py-8 text-center">
                    <p className="text-sm text-slate-500 mb-3">
                      Belum ada paket kredit yang dibuat.
                    </p>
                    <button
                      type="button"
                      onClick={openCreateForm}
                      className="inline-flex items-center justify-center px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold bg-sky-600 text-white shadow-sm hover:bg-sky-700 active:scale-95 transition"
                    >
                      Tambah Paket Pertama
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Desktop: tabel  */}
                    <div className="hidden md:block overflow-x-auto -mx-3 sm:mx-0">
                      <table className="min-w-full text-sm">
                        <thead>
                          <tr className="text-xs text-slate-500 border-b border-slate-100">
                            <th className="text-left font-semibold py-2 px-3">
                              Paket
                            </th>
                            <th className="text-left font-semibold py-2 px-3">
                              Harga
                            </th>
                            <th className="text-left font-semibold py-2 px-3">
                              Kredit
                            </th>
                            <th className="text-left font-semibold py-2 px-3">
                              Fitur
                            </th>
                            <th className="text-left font-semibold py-2 px-3">
                              Status
                            </th>
                            <th className="text-right font-semibold py-2 px-3">
                              Aksi
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {packages.map((pkg) => {
                            const pkgFeatures = featuresByPackageId.get(pkg.id) || [];
                            return (
                              <tr
                                key={pkg.id}
                                className="border-b border-slate-50 hover:bg-slate-50/60"
                              >
                                <td className="py-3 px-3 align-top">
                                  <div className="font-semibold text-slate-800 text-sm">
                                    {pkg.name}
                                  </div>
                                  <div className="flex flex-wrap gap-1 mt-1">
                                    {pkg.populer && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
                                        Populer
                                      </span>
                                    )}
                                    {pkg.beranda && (
                                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800">
                                        Tampil di Beranda
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td className="py-3 px-3 align-top text-slate-800">
                                  <div className="flex flex-col">
                                    {pkg.original_price && Number(pkg.original_price) > Number(pkg.price) && (
                                      <span className="text-[10px] text-slate-400 line-through">
                                        {formatRupiah(pkg.original_price)}
                                      </span>
                                    )}
                                    <span>{formatRupiah(pkg.price)}</span>
                                  </div>
                                </td>
                                <td className="py-3 px-3 align-top text-slate-800">
                                  {pkg.base_credits} kredit
                                </td>
                                <td className="py-3 px-3 align-top">
                                  {pkg.is_custom_button && pkg.custom_features ? (
                                    <div className="flex flex-wrap gap-1">
                                      {pkg.custom_features.split(',').map((txt, i) => (
                                        <span
                                          key={i}
                                          className="inline-flex px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100 text-[11px]"
                                        >
                                          {txt.trim()}
                                        </span>
                                      ))}
                                    </div>
                                  ) : pkgFeatures.length === 0 ? (
                                    <span className="text-[11px] text-slate-400 italic">
                                      Belum ada fitur
                                    </span>
                                  ) : (
                                    <div className="flex flex-wrap gap-1">
                                      {pkgFeatures.map((f) => (
                                        <span
                                          key={f.id}
                                          className="inline-flex px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100 text-[11px]"
                                        >
                                          {f.name}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </td>
                                <td className="py-3 px-3 align-top">
                                  <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${pkg.is_active
                                      ? "bg-emerald-100 text-emerald-700"
                                      : "bg-slate-100 text-slate-500"
                                      }`}
                                  >
                                    {pkg.is_active ? "Aktif" : "Nonaktif"}
                                  </span>
                                </td>
                                <td className="py-3 px-3 align-top text-right">
                                  <div className="flex justify-end gap-2">
                                    <button
                                      type="button"
                                      onClick={() => openEditForm(pkg)}
                                      className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title="Edit"
                                    >
                                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleDelete(pkg)}
                                      disabled={deletingId === pkg.id}
                                      className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors disabled:opacity-50"
                                      title="Hapus"
                                    >
                                      {deletingId === pkg.id ? <div className="animate-spin h-3.5 w-3.5 border-2 border-rose-600 border-t-transparent rounded-full" /> : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Mobile: card list */}
                    <div className="grid grid-cols-1 gap-3 md:hidden">
                      {packages.map((pkg) => {
                        const pkgFeatures = featuresByPackageId.get(pkg.id) || [];
                        return (
                          <div
                            key={pkg.id}
                            className="rounded-2xl border border-slate-100 bg-slate-50/60 p-3 shadow-sm"
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div>
                                <div className="text-sm font-semibold text-slate-900">
                                  {pkg.name}
                                </div>
                                <div className="text-xs text-slate-500">
                                  {pkg.base_credits} kredit ·{" "}
                                  {pkg.original_price && Number(pkg.original_price) > Number(pkg.price) ? (
                                    <span>
                                      <span className="text-slate-400 line-through mr-1">
                                        {formatRupiah(pkg.original_price)}
                                      </span>
                                      {formatRupiah(pkg.price)}
                                    </span>
                                  ) : (
                                    formatRupiah(pkg.price)
                                  )}
                                </div>
                              </div>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${pkg.is_active
                                  ? "bg-emerald-100 text-emerald-700"
                                  : "bg-slate-100 text-slate-500"
                                  }`}
                              >
                                {pkg.is_active ? "Aktif" : "Nonaktif"}
                              </span>
                            </div>

                            <div className="flex flex-wrap gap-1 mt-2">
                              {pkg.populer && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800">
                                  Populer
                                </span>
                              )}
                              {pkg.beranda && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800">
                                  Beranda
                                </span>
                              )}
                            </div>

                            <div className="mt-2">
                              <p className="text-[11px] font-semibold text-slate-600 mb-1">
                                Fitur:
                              </p>
                              {pkg.is_custom_button && pkg.custom_features ? (
                                <div className="flex flex-wrap gap-1">
                                  {pkg.custom_features.split(',').map((txt, i) => (
                                    <span
                                      key={i}
                                      className="inline-flex px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-700 border border-purple-100 text-[11px]"
                                    >
                                      {txt.trim()}
                                    </span>
                                  ))}
                                </div>
                              ) : pkgFeatures.length === 0 ? (
                                <p className="text-[11px] text-slate-400 italic">
                                  Belum ada fitur
                                </p>
                              ) : (
                                <div className="flex flex-wrap gap-1">
                                  {pkgFeatures.map((f) => (
                                    <span
                                      key={f.id}
                                      className="inline-flex px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-100 text-[10px]"
                                    >
                                      {f.name}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            <div className="mt-3 flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEditForm(pkg)}
                                className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Edit"
                              >
                                <Icons.Pencil size={16} />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleDelete(pkg)}
                                disabled={deletingId === pkg.id}
                                className="p-1.5 text-slate-500 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                                title="Hapus"
                              >
                                {deletingId === pkg.id ? <div className="animate-spin h-3.5 w-3.5 border-2 border-rose-600 border-t-transparent rounded-full" /> : <Icons.Trash2 size={16} />}
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>
      </div>

      {/* MODAL FORM TAMBAH/EDIT */}
      {formOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center px-3 sm:px-4">
          {/* backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/40"
            onClick={saving ? undefined : closeForm}
          />
          {/* card dengan scroll sendiri */}
          <div className="relative w-full max-w-lg max-h-[85vh] bg-white rounded-2xl shadow-xl border border-slate-100 p-4 sm:p-6 overflow-y-auto">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div>
                <h2 className="text-base sm:text-lg font-semibold text-slate-900">
                  {formMode === "create" ? "Tambah Paket Baru" : "Edit Paket"}
                </h2>
                <p className="text-xs sm:text-sm text-slate-500">
                  Atur nama paket, harga, jumlah kredit, status, dan fitur-fitur
                  yang termasuk.
                </p>
              </div>
              <button
                type="button"
                onClick={closeForm}
                disabled={saving}
                className="p-1.5 rounded-full hover:bg-slate-100 text-slate-500 disabled:opacity-50"
                aria-label="Tutup"
              >
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <path
                    d="M6 18L18 6M6 6l12 12"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-slate-700">
                    Nama Paket
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formState.name}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    placeholder="Contoh: Paket Starter"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-slate-700">
                    Harga (IDR)
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formState.price}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    min="0"
                    step="1000"
                    placeholder="Contoh: 50000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-slate-700">
                    Jumlah Kredit
                  </label>
                  <input
                    type="number"
                    name="base_credits"
                    value={formState.base_credits}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    min="1"
                    step="1"
                    placeholder="Contoh: 100"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="block text-xs font-medium text-slate-700">
                    Status & Label
                  </label>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        name="is_active"
                        checked={formState.is_active}
                        onChange={handleChange}
                        className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      />
                      <span>Aktif</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        name="populer"
                        checked={formState.populer}
                        onChange={handleChange}
                        className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      />
                      <span>Populer</span>
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        name="beranda"
                        checked={formState.beranda}
                        onChange={handleChange}
                        className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                      />
                      <span>Tampil di Beranda</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Harga Asli (Diskon) */}
              <div className="space-y-1.5">
                <label className="block text-xs font-medium text-slate-700">
                  Harga Asli (Untuk Tampilan Coret)
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="original_price"
                    value={formState.original_price || ""}
                    onChange={handleChange}
                    className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    placeholder="Kosongkan jika tidak ada diskon"
                  />
                  <p className="text-[10px] text-slate-500 mt-1">
                    Jika diisi lebih besar dari Harga, maka harga ini akan dicoret.
                  </p>
                </div>
              </div>

              {/* Custom Button Settings */}
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-3">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-700 flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="is_custom_button"
                      checked={formState.is_custom_button}
                      onChange={handleChange}
                      className="rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                    />
                    Mode Tombol Kustom (Hubungi Kami)
                  </label>
                </div>

                {formState.is_custom_button && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 animate-in fade-in slide-in-from-top-2">
                    <div className="space-y-1.5">
                      <label className="block text-xs font-medium text-slate-700">
                        Teks Tombol
                      </label>
                      <input
                        type="text"
                        name="button_text"
                        value={formState.button_text || ""}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                        placeholder="Misal: Hubungi Kami"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <label className="block text-xs font-medium text-slate-700">
                        Link URL
                      </label>
                      <input
                        type="text"
                        name="button_url"
                        value={formState.button_url || ""}
                        onChange={handleChange}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                        placeholder="https://wa.me/..."
                      />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <label className="block text-xs font-medium text-slate-700">
                        Deskripsi/Fitur Kustom (Pisahkan dengan koma)
                      </label>
                      <textarea
                        name="custom_features"
                        value={formState.custom_features || ""}
                        onChange={handleChange}
                        rows={3}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                        placeholder="Contoh: Admin 24 Jam, Konsultasi Gratis, Garansi Uang Kembali"
                      />
                      <p className="text-[10px] text-slate-500">
                        Teks ini akan muncul sebagai list fitur di halaman Topup.
                      </p>
                    </div>
                  </div>
                )}
              </div>


              {/* Show feature selection only if NOT custom button mode */}
              {!formState.is_custom_button && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <label className="block text-xs font-medium text-slate-700">
                        Fitur dalam Paket
                      </label>
                      <p className="text-[11px] text-slate-500">
                        Centang fitur yang termasuk dalam paket ini. Data diambil dari
                        tabel <code className="font-mono text-[10px] bg-slate-100 px-1 rounded">app_features</code>.
                      </p>
                    </div>

                    {/* tombol centang semua */}
                    <button
                      type="button"
                      onClick={handleToggleAllFeatures}
                      disabled={features.length === 0}
                      className="inline-flex items-center px-2 py-1 rounded-lg border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-60"
                    >
                      {allFeaturesSelected ? "Kosongkan semua" : "Centang semua"}
                    </button>
                  </div>

                  {/* scroll khusus list fitur */}
                  <div className="max-h-56 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 px-2 py-1.5">
                    {features.length === 0 ? (
                      <p className="text-[11px] text-slate-400 italic">
                        Belum ada data fitur di tabel <code>app_features</code>. Tambahkan
                        fitur terlebih dahulu.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {features.map((f) => {
                          const checked = formState.featureIds.includes(f.id);
                          return (
                            <label
                              key={f.id}
                              className="flex items-start gap-2 text-[11px]"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleFeatureId(f.id)}
                                className="mt-[2px] rounded border-slate-300 text-sky-600 focus:ring-sky-500"
                              />
                              <div>
                                <div className="font-semibold text-slate-800">
                                  {f.name}
                                </div>
                                {f.description && (
                                  <div className="text-[10px] text-slate-500">
                                    {f.description}
                                  </div>
                                )}
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between pt-2">
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={saving}
                  className="px-3 py-2 rounded-xl border border-slate-200 bg-white text-xs sm:text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 rounded-xl bg-sky-600 text-white text-xs sm:text-sm font-semibold shadow-sm hover:bg-sky-700 active:scale-95 transition disabled:opacity-60"
                >
                  {saving
                    ? "Menyimpan..."
                    : formMode === "create"
                      ? "Simpan Paket"
                      : "Update Paket"}
                </button>
              </div >
            </form >
          </div >
        </div >
      )}
    </div >
  );
};

export default AdminPackagesPage;
